import {
  type AskPunttAgentStep,
  type AskPunttContentBlock,
  type AskPunttMessage,
  type AskPunttUserQuestion,
} from "@mg/schemas/src/christo/askPuntt";
import { type ReviewFiles } from "@mg/schemas/src/commons";
import { CircleNotch, File, ThumbsDown, ThumbsUp } from "@phosphor-icons/react";
import { Flex, Text } from "@radix-ui/themes";
import { useEffect, useMemo, useRef, useState } from "react";

import { AgentSteps } from "./AgentSteps";
import { ChatVideoAttachment } from "./ChatVideoAttachment";
import { PunttAvatar } from "./PunttAvatar";
import {
  type QuestionAttachment,
  type QuestionFormHandle,
  QuestionForm,
} from "./QuestionForm";
import { SearchProgress } from "./SearchProgress";
import { StructuredResponse } from "./StructuredResponse";
import { ThinkingState } from "./ThinkingState";

import { AvatarWithInitials } from "../../../components/AvatarWithInitials";
import { Expandable } from "../../../components/Expandable";
import { Markdown } from "../../../components/Markdown";
import { useAnalytics } from "../../../utils/analytics";
import { downloadAsset } from "../../../utils/http";
import { useSubmitAskPunttFeedback } from "../../../utils/queries/askPuntt";
import {
  MENTION_HIGHLIGHT_CLASS,
  renderMentionSegments,
  resolveMentionActorsForFeedMessage,
} from "../chatUtils";

/** Short "5:04 PM" timestamp for the feed presentation header. */
function formatFeedTime(iso?: string): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleTimeString(undefined, {
    hour: "numeric",
    minute: "2-digit",
  });
}

// ─── Display segment types for interleaved text/tool rendering ───────────────

type DisplaySegment =
  | { type: "text"; content: string }
  | { type: "tools"; steps: AskPunttAgentStep[] };

/**
 * Strip known step contents from the full content string to extract only
 * trailing text not covered by any agentStep. The backend joins rounds
 * with "\n\n", so we peel off known prefixes in order.
 *
 * If prefix stripping fails (e.g. content was transformed by canvas
 * stripping), fall back to checking whether displayContent is fully
 * covered by the known step texts to avoid rendering duplicates.
 */
function computeFinalContent(
  fullContent: string,
  knownContents: string[],
): string {
  if (!knownContents.length) return fullContent;

  // Fast path: sequential prefix stripping
  let remaining = fullContent;
  let stripped = 0;
  for (const known of knownContents) {
    if (remaining.startsWith(known)) {
      remaining = remaining.slice(known.length);
      // Consume the round separator between known contents
      if (remaining.startsWith("\n\n")) {
        remaining = remaining.slice(2);
      }
      stripped++;
    }
  }
  // Only return from fast path if ALL known contents were stripped.
  // Partial stripping means whitespace mismatch (e.g. streamingContent
  // has trailing \n\n from the LLM that agentSteps .strip()'d away),
  // so fall through to the trimmed path which handles that.
  if (stripped === knownContents.length) return remaining.trim();

  // Secondary path: try trimmed matching when exact prefix fails
  // (handles whitespace differences between streamingContent and step text)
  remaining = fullContent;
  stripped = 0;
  for (const known of knownContents) {
    const trimmedKnown = known.trim();
    const trimmedRemaining = remaining.trimStart();
    if (trimmedKnown && trimmedRemaining.startsWith(trimmedKnown)) {
      const idx = remaining.indexOf(trimmedKnown);
      remaining = remaining.slice(idx + trimmedKnown.length);
      if (remaining.startsWith("\n\n")) {
        remaining = remaining.slice(2);
      }
      stripped++;
    }
  }
  if (stripped === knownContents.length) return remaining.trim();

  // Fallback: prefix stripping failed (content transformed, e.g. canvas
  // blocks stripped from streamingContent but present in step text).
  // Suppress trailing content entirely when text steps exist to prevent
  // duplicates. Partial streaming text for the active round still shows
  // because its step has empty content (not in knownContents).
  return "";
}

/**
 * Builds an interleaved sequence of text bubbles and tool step groups.
 *
 * Three paths:
 * 1. contentBlocks present (completed message) — use blocks directly
 * 2. agentSteps with "text"/"thinking" markers (streaming) — split on them
 * 3. No steps — single text bubble (backward compat)
 */
function computeDisplaySegments(
  agentSteps: AskPunttAgentStep[] | undefined,
  displayContent: string,
  contentBlocks?: AskPunttContentBlock[] | null,
): DisplaySegment[] {
  // Path 1: authoritative contentBlocks (completed messages)
  if (contentBlocks?.length) {
    return contentBlocks
      .map((block): DisplaySegment | null => {
        if (block.type === "text")
          return { type: "text" as const, content: block.content };
        if (block.type === "tool_activity") {
          const [start, end] = block.stepRange;
          return {
            type: "tools" as const,
            steps: (agentSteps ?? []).slice(start, end),
          };
        }
        return null;
      })
      .filter(Boolean) as DisplaySegment[];
  }

  // Path 3: no steps — single text bubble
  if (!agentSteps?.length) {
    if (displayContent) return [{ type: "text", content: displayContent }];
    return [];
  }

  // Path 2: reconstruct from agentSteps (streaming + legacy)
  const segments: DisplaySegment[] = [];
  let currentToolSteps: AskPunttAgentStep[] = [];
  const knownContents: string[] = [];

  for (const step of agentSteps) {
    if (step.type === "thinking" || step.type === "text") {
      // Flush accumulated tool steps
      if (currentToolSteps.length) {
        segments.push({ type: "tools", steps: [...currentToolSteps] });
        currentToolSteps = [];
      }
      // Each thinking/text step becomes its own text bubble
      if (step.content.trim()) {
        segments.push({ type: "text", content: step.content });
        knownContents.push(step.content);
      }
    } else {
      currentToolSteps.push(step);
    }
  }

  // Flush remaining tool steps
  if (currentToolSteps.length) {
    segments.push({ type: "tools", steps: [...currentToolSteps] });
  }

  // Trailing content not covered by any step (streaming text or legacy)
  if (displayContent) {
    const finalContent = computeFinalContent(displayContent, knownContents);
    if (finalContent) {
      segments.push({ type: "text", content: finalContent });
    }
  }

  return segments;
}

/** Image with loading ghost - shows skeleton until image loads, reserves space */
function ImageWithLoader({
  src,
  alt,
  href,
}: {
  src: string;
  alt: string;
  href: string;
}) {
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState(false);

  return (
    <div className="relative block min-h-32 min-w-32 overflow-hidden rounded-xl shadow-sm ring-1 ring-puntt-neutral-gray-5">
      {!loaded && !error && (
        <div
          className="absolute inset-0 z-10 flex items-center justify-center"
          aria-hidden
        >
          <div className="h-32 w-48 animate-pulse rounded-lg bg-puntt-neutral-gray-11" />
        </div>
      )}
      {error ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="chat-image-attachment-error"
          aria-label={`Open ${alt} in a new tab`}
          className="flex h-24 items-center justify-center rounded-lg border border-puntt-neutral-gray-10 bg-puntt-neutral-gray-11/50 px-4 text-puntt-neutral-gray-11"
        >
          <Text size="2">Image failed to load — click to open</Text>
        </a>
      ) : (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="chat-image-attachment-link"
          aria-label={`Open ${alt} in a new tab`}
          className="block"
        >
          <img
            src={src}
            alt={alt}
            className={`max-h-48 max-w-full object-contain transition-opacity duration-200 ${loaded ? "opacity-100" : "opacity-0"}`}
            onLoad={() => setLoaded(true)}
            onError={() => setError(true)}
          />
        </a>
      )}
    </div>
  );
}

function FileAttachmentButton({ url, name }: { url: string; name?: string }) {
  const [downloading, setDownloading] = useState(false);

  const handleClick = async () => {
    if (downloading) return;
    setDownloading(true);
    try {
      await downloadAsset(url, name ?? undefined);
    } catch (err) {
      console.error("Failed to download attachment", err);
    } finally {
      setDownloading(false);
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={downloading}
      data-testid="chat-file-attachment"
      // text-left overrides the <button> default of text-align: center so
      // wrapped filenames stay left-aligned next to the file icon instead
      // of drifting into the middle of the chip.
      className="flex w-full min-w-0 max-w-full items-start gap-2 overflow-hidden rounded-xl bg-puntt-neutral-gray-5 px-2 py-1.5 text-left text-puntt-neutral-gray-12 hover:underline disabled:opacity-60"
      aria-label={`Download attachment ${name ?? ""}`.trim()}
    >
      {downloading ? (
        <CircleNotch size={16} className="mt-0.5 shrink-0 animate-spin" />
      ) : (
        <File size={16} className="mt-0.5 shrink-0" />
      )}
      <Text size="2" className="line-clamp-2 min-w-0 flex-1 break-all">
        {name ?? "Download"}
      </Text>
    </button>
  );
}

// Robot icon from the existing BrandCheck ChatMessage
export function RobotIcon() {
  return (
    <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-puntt-accent-3 ring-1 ring-puntt-accent-6/40">
      <svg
        width="20"
        height="19"
        viewBox="0 0 30 28"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M22.4939 5.67486H15.8328V2.34435C15.8328 2.12352 15.7451 1.91174 15.589 1.75559C15.4328 1.59944 15.221 1.51172 15.0002 1.51172C14.7794 1.51172 14.5676 1.59944 14.4114 1.75559C14.2553 1.91174 14.1676 2.12352 14.1676 2.34435V5.67486H7.50654C6.62323 5.67486 5.7761 6.02576 5.15151 6.65035C4.52692 7.27494 4.17603 8.12207 4.17603 9.00538V20.6622C4.17603 21.5455 4.52692 22.3926 5.15151 23.0172C5.7761 23.6418 6.62323 23.9927 7.50654 23.9927H22.4939C23.3772 23.9927 24.2243 23.6418 24.8489 23.0172C25.4735 22.3926 25.8244 21.5455 25.8244 20.6622V9.00538C25.8244 8.12207 25.4735 7.27494 24.8489 6.65035C24.2243 6.02576 23.3772 5.67486 22.4939 5.67486Z"
          fill="rgb(var(--puntt-blue-9))"
        />
        <circle
          cx="10.428"
          cy="13.5947"
          r="2.91667"
          stroke="white"
          strokeWidth="1.66667"
        />
        <circle
          cx="19.5947"
          cy="13.5947"
          r="2.91667"
          stroke="white"
          strokeWidth="1.66667"
        />
      </svg>
    </div>
  );
}

interface ChatMessageProps {
  message: AskPunttMessage;
  /** Chat ID for feedback submission */
  chatId?: string;
  /**
   * Ticket review files for this chat's active ticket. When present,
   * video attachments play from the same HLS playlist the ticket uses
   * (`reviewFiles[0].source`) because the raw S3 upload URL is revoked
   * by the ingest pipeline once transcoding finishes.
   */
  reviewFiles?: ReviewFiles[];
  /**
   * Map of attachment S3 key → browser blob URL pointing at the
   * original local file. When present, previews for freshly-uploaded
   * video/image attachments render straight from disk, avoiding the
   * CDN entirely. Only populated for messages sent in the current
   * session; rehydrated messages after a page refresh fall back to
   * the HLS/remote resolution logic.
   */
  localPreviewUrls?: Record<string, string>;
  /** True when the backend is still generating this message */
  isStreaming?: boolean;
  /** True when this is the last assistant message (enables interactive form) */
  isLastAssistant?: boolean;
  /** Called when user submits answers via the multi-question form */
  onSubmitAnswers?: (
    formattedAnswers: string,
    attachments?: QuestionAttachment[],
  ) => void;
  /** Disable the question form (e.g. while sending) */
  formDisabled?: boolean;
  /** Ref forwarded to the active QuestionForm for external submit triggers */
  questionFormRef?: React.Ref<QuestionFormHandle>;
  /** Called when the form's "has any answer" state changes */
  onAnswerChange?: (hasAnswer: boolean) => void;
  /**
   * When true, suppresses the inline QuestionForm render (it's shown in the
   * input area instead). Non-last messages still render read-only as usual.
   */
  hideInlineQuestionForm?: boolean;
  /**
   * True when the preceding user message carried file attachments. Forwarded
   * to ThinkingState so the pre-agent read pause shows "Reading attachments..."
   * instead of the generic "Starting up...".
   */
  precedingUserHasAttachments?: boolean;
  /**
   * Visual frame for the message. "chat" (default) is the classic chat layout
   * (right-aligned user bubble, left assistant body). "feed" renders a
   * Slack-style row — left avatar + name + role + timestamp header with a
   * left-aligned body — for the Ask Puntt Projects feed, while keeping all the
   * live machinery (streaming, agent steps, canvas, attachments) intact.
   */
  presentation?: "chat" | "feed";
  /** Display name for USER turns in the feed presentation (e.g. the viewer). */
  feedActorName?: string;
  /** Avatar URL for USER turns in the feed presentation. */
  feedActorAvatar?: string | null;
  /**
   * When true in feed presentation, the current viewer's user message gets the
   * gray bubble background. Other members' messages stay unstyled.
   */
  feedOwnUserMessage?: boolean;
  /**
   * Project members for the feed presentation, used to resolve mention ids on
   * a user message to display names when highlighting `@Name` chips.
   */
  feedMembers?: {
    _id: string;
    name?: string;
    email?: string;
    avatar?: string | null;
  }[];
  /**
   * Feed-only. When true, the message body is collapsed to a short clamped
   * preview with a "Show more" / "Show less" toggle. Set for every feed message
   * except the most recent (which stays fully expanded as the current focus).
   */
  collapsible?: boolean;
}

export function ChatMessage({
  message,
  chatId,
  reviewFiles,
  localPreviewUrls,
  isStreaming,
  isLastAssistant,
  onSubmitAnswers,
  formDisabled,
  questionFormRef,
  onAnswerChange,
  hideInlineQuestionForm,
  precedingUserHasAttachments,
  presentation = "chat",
  feedActorName,
  feedActorAvatar,
  feedOwnUserMessage = false,
  feedMembers,
  collapsible = false,
}: ChatMessageProps) {
  const analytics = useAnalytics("ask_puntt_chat");
  const feedbackMutation = useSubmitAskPunttFeedback(chatId ?? "");
  const isCompleted = message.status !== "pending" && !!message.content;
  const showFeedback =
    message.role === "assistant" && isCompleted && !isStreaming && chatId;

  // Display streamingContent while content is being generated, final content once done
  const displayContent = message.content || message.streamingContent || "";
  const hasContent = !!message.content;
  const showStreamingCursor =
    isStreaming && !hasContent && !!message.streamingContent;

  // Build interleaved text/tool segments for rendering
  const segments = useMemo(
    () =>
      computeDisplaySegments(
        message.agentSteps,
        displayContent,
        message.contentBlocks,
      ),
    [message.agentSteps, displayContent, message.contentBlocks],
  );
  const hasSegments = segments.length > 0;

  // Streaming cursor fade-out
  const wasStreamingRef = useRef(showStreamingCursor);
  const [cursorFading, setCursorFading] = useState(false);
  const [cursorVisible, setCursorVisible] = useState(showStreamingCursor);

  useEffect(() => {
    if (wasStreamingRef.current && !showStreamingCursor) {
      setCursorFading(true);
      const timer = setTimeout(() => {
        setCursorVisible(false);
        setCursorFading(false);
      }, 200);
      return () => clearTimeout(timer);
    }
    if (showStreamingCursor) {
      setCursorVisible(true);
      setCursorFading(false);
    }
    wasStreamingRef.current = showStreamingCursor;
  }, [showStreamingCursor]);

  // Progressive structured response reveal
  const [revealedSections, setRevealedSections] = useState(0);
  const structuredRevealedRef = useRef(false);

  useEffect(() => {
    if (
      hasContent &&
      message.structuredContent &&
      !structuredRevealedRef.current
    ) {
      structuredRevealedRef.current = true;
      const timers = [0, 150, 300, 500].map((delay, i) =>
        setTimeout(() => setRevealedSections(i + 1), delay),
      );
      return () => timers.forEach(clearTimeout);
    }
  }, [hasContent, message.structuredContent]);

  // Resolve @tokens to actors so feed user messages render highlighted chips.
  const mentionActors = useMemo(
    () =>
      resolveMentionActorsForFeedMessage(
        message.content,
        message.mentions,
        feedMembers,
      ),
    [message.content, message.mentions, feedMembers],
  );

  // Split into text vs. attachments so the feed presentation can clamp only the
  // text while always rendering attachments in full (line-clamping an <img>
  // crops it and strands the "Show more" toggle under a sliced picture).
  const userTextContent =
    mentionActors.length > 0 ? (
      <p
        data-testid="chat-message-feed-content"
        className="whitespace-pre-wrap text-puntt-neutral-gray-12"
      >
        {renderMentionSegments(message.content, mentionActors).map((seg, i) =>
          seg.type === "mention" ? (
            <span
              key={i}
              data-testid={`chat-message-mention-${seg.id}`}
              className={MENTION_HIGHLIGHT_CLASS}
            >
              {seg.label}
            </span>
          ) : (
            <span key={i}>{seg.text}</span>
          ),
        )}
      </p>
    ) : (
      <Markdown className="text-puntt-neutral-gray-12 [&_strong]:font-semibold [&_strong]:text-puntt-neutral-gray-12">
        {message.content}
      </Markdown>
    );

  const userAttachments =
    message.attachments && message.attachments.length > 0 ? (
      <Flex direction="column" gap="2" className="mt-2">
        {message.attachments.map((att, idx) => {
          const mediaUrl =
            att.source || (att.key ? `https://static.puntt.ai/${att.key}` : "");
          return (
            <div key={att.key ?? idx} className="min-w-0">
              {att.type === "image" && mediaUrl && (
                <ImageWithLoader
                  src={(att.key && localPreviewUrls?.[att.key]) || mediaUrl}
                  alt={att.name ?? "Attachment"}
                  href={mediaUrl}
                />
              )}
              {att.type === "video" && mediaUrl && (
                <ChatVideoAttachment
                  src={
                    (att.key && localPreviewUrls?.[att.key]) ||
                    reviewFiles?.[0]?.source ||
                    mediaUrl
                  }
                  name={att.name}
                />
              )}
              {att.type === "file" && (mediaUrl || att.source) && (
                <FileAttachmentButton
                  url={mediaUrl || att.source || ""}
                  name={att.name}
                />
              )}
            </div>
          );
        })}
      </Flex>
    ) : null;

  const userContent = (
    <>
      {userTextContent}
      {userAttachments}
    </>
  );

  const assistantBody = (
    <div className="min-w-0 space-y-3">
      {/* Interleaved text bubbles and tool step groups */}
      {(() => {
        const lastToolIdx = segments.reduce(
          (acc, s, i) => (s.type === "tools" ? i : acc),
          -1,
        );
        return segments.map((segment, idx) => {
          if (segment.type === "tools") {
            return (
              <div key={`tools-${idx}`} className="space-y-3">
                <AgentSteps
                  steps={segment.steps}
                  isStreaming={
                    !!isStreaming && !hasContent && idx === lastToolIdx
                  }
                  message={message}
                />
                {idx === lastToolIdx &&
                  message.searchProgress &&
                  message.searchProgress.length > 0 && (
                    <SearchProgress searchProgress={message.searchProgress} />
                  )}
              </div>
            );
          }
          // Text segment
          const isLastSegment = idx === segments.length - 1;
          const showCursor =
            isLastSegment && (showStreamingCursor || cursorVisible);
          return (
            <div key={`text-${idx}`} data-testid="chat-message-content">
              <div className="text-puntt-neutral-gray-12">
                <Markdown>{segment.content}</Markdown>
                {showCursor && (
                  <span
                    className={`ml-0.5 inline-block h-4 w-[3px] animate-pulse rounded-full bg-puntt-accent-9 transition-opacity duration-200 motion-reduce:animate-none ${cursorFading ? "opacity-0" : "opacity-100"}`}
                  />
                )}
              </div>
            </div>
          );
        });
      })()}

      {/* Thinking state below content (like Claude) -- only when no text visible */}
      {!hasSegments && (
        <ThinkingState
          message={message}
          precedingUserHasAttachments={precedingUserHasAttachments}
        />
      )}

      {/* Feedback buttons */}
      {showFeedback && (
        <Flex gap="1" className="ml-1">
          <button
            data-testid="chat-feedback-thumbs-up"
            onClick={() => {
              analytics.capture("ask_puntt_feedback", {
                feedback: "thumbs_up",
                message_id: message._id,
                chat_id: chatId,
              });
              feedbackMutation.mutate({
                messageId: message._id,
                feedback: "thumbs_up",
              });
            }}
            className={`rounded-md p-1 transition-colors ${
              message.feedback === "thumbs_up"
                ? "bg-puntt-accent-3 text-puntt-accent-11"
                : "text-puntt-neutral-gray-8 hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-11"
            }`}
            aria-label="Thumbs up"
          >
            <ThumbsUp
              size={16}
              weight={message.feedback === "thumbs_up" ? "fill" : "regular"}
            />
          </button>
          <button
            data-testid="chat-feedback-thumbs-down"
            onClick={() => {
              analytics.capture("ask_puntt_feedback", {
                feedback: "thumbs_down",
                message_id: message._id,
                chat_id: chatId,
              });
              feedbackMutation.mutate({
                messageId: message._id,
                feedback: "thumbs_down",
              });
            }}
            className={`rounded-md p-1 transition-colors ${
              message.feedback === "thumbs_down"
                ? "bg-puntt-red-3 text-puntt-red-9"
                : "text-puntt-neutral-gray-8 hover:bg-puntt-neutral-gray-3 hover:text-puntt-neutral-gray-11"
            }`}
            aria-label="Thumbs down"
          >
            <ThumbsDown
              size={16}
              weight={message.feedback === "thumbs_down" ? "fill" : "regular"}
            />
          </button>
        </Flex>
      )}

      {/* Structured response — only show when final content is set */}
      {hasContent && message.structuredContent && (
        <StructuredResponse
          content={message.structuredContent}
          maxSections={revealedSections}
        />
      )}

      {/* Multi-question form — hidden for the active message (shown in input area); read-only for history */}
      {hasContent &&
        message.userQuestions &&
        message.userQuestions.length > 0 &&
        !hideInlineQuestionForm && (
          <QuestionForm
            data-testid="chat-message-question-form"
            ref={isLastAssistant ? questionFormRef : undefined}
            questions={message.userQuestions as AskPunttUserQuestion[]}
            onSubmit={onSubmitAnswers ?? (() => {})}
            disabled={formDisabled}
            readOnly={!isLastAssistant}
            hideSubmitButton={isLastAssistant && !!questionFormRef}
            onAnswerChange={isLastAssistant ? onAnswerChange : undefined}
          />
        )}
    </div>
  );

  // Feed presentation: a Slack-style row (left avatar + name + role +
  // timestamp) wrapping the SAME live body, so the project feed keeps full
  // fidelity (streaming, agent steps, canvas, attachments) but reads like the
  // project timeline instead of a centered chat bubble.
  if (presentation === "feed") {
    const isUser = message.role === "user";
    const name = isUser ? feedActorName || "Member" : "Puntt";
    const role = isUser ? undefined : "AI reviewer";
    const time = formatFeedTime(message.createdAt);
    return (
      <article
        className="relative mb-4 flex gap-3"
        data-testid={isUser ? "chat-message-user" : "chat-message-assistant"}
        data-role={message.role}
        data-presentation="feed"
      >
        <div className="shrink-0 pt-0.5">
          {isUser ? (
            <AvatarWithInitials
              size={8}
              name={name}
              avatar={feedActorAvatar ?? undefined}
            />
          ) : (
            <PunttAvatar size={8} />
          )}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline gap-2">
            <span className="text-[13px] font-semibold text-puntt-neutral-gray-12">
              {name}
            </span>
            {role && (
              <span className="text-[11px] text-puntt-neutral-gray-10">
                {role}
              </span>
            )}
            {time && (
              <span
                className="shrink-0 text-[11px] text-puntt-neutral-gray-9"
                data-testid="feed-message-timestamp"
              >
                {time}
              </span>
            )}
          </div>
          {isUser ? (
            <div
              className={
                feedOwnUserMessage
                  ? "mt-1 space-y-2 rounded-lg bg-puntt-neutral-gray-3 px-3 py-2"
                  : "mt-1 space-y-2"
              }
              data-testid="chat-message-content"
            >
              {/* Clamp only the text; attachments always render in full below. */}
              {collapsible ? (
                <Expandable
                  maxLines={4}
                  size="2"
                  allowCollapse
                  buttonText="Show more"
                  collapseButtonText="Show less"
                  testId="feed-message"
                >
                  {userTextContent}
                </Expandable>
              ) : (
                userTextContent
              )}
              {userAttachments}
            </div>
          ) : (
            <div className="mt-1">
              {collapsible ? (
                <Expandable
                  maxLines={4}
                  size="2"
                  allowCollapse
                  buttonText="Show more"
                  collapseButtonText="Show less"
                  testId="feed-message"
                >
                  {assistantBody}
                </Expandable>
              ) : (
                assistantBody
              )}
            </div>
          )}
        </div>
      </article>
    );
  }

  if (message.role === "user") {
    return (
      <div
        className="relative mb-4"
        data-testid="chat-message-user"
        data-role="user"
      >
        <Flex justify="end">
          <div
            className="max-w-[70%] space-y-2 rounded-2xl bg-puntt-neutral-gray-3 px-4 py-3"
            data-testid="chat-message-content"
          >
            {userContent}
          </div>
        </Flex>
      </div>
    );
  }

  return (
    <div
      className="relative mb-4"
      data-testid="chat-message-assistant"
      data-role="assistant"
    >
      {assistantBody}
    </div>
  );
}
